import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUserFromOkta, updateUserInOkta } from '@api/profiles';
import { message } from 'antd';
import { AxiosError } from 'axios'; // Import AxiosError type

const staleTime = 300000;
const messageTime = 7;
const messageSettleTime = 5000;
const oktaQueryKey = ['get-profile-okta', 'ei'];

export interface Person {
  role: string;
  userName: string;
  customerName: string;
}

export interface OktaInfo {
  user_id: string;
  isFederated?: string;
  firstName?: string;
  lastName?: string;
  mobilePhone?: string;
  countryCode?: string;
  organization?: string;
  secondEmail?: string;
  title?: string;
  department?: string;
  login?: string;
  email?: string;
}

export const useOktaDetails = (userName: string, standAlone: boolean) => {
  const response = useQuery(
    oktaQueryKey,
    () => {
      return getUserFromOkta<OktaInfo>(userName, standAlone);
    },
    {
      retry: 3,
      refetchOnWindowFocus: false,
      staleTime: staleTime, // 5m
    },
  );

  return response;
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const loadingMessageKey = 'loadingMessage';

  return useMutation(
    (formData: OktaInfo) => {
      const { email, ...rest } = formData;
      return updateUserInOkta(rest, email);
    },
    {
      onMutate: () => {
        message.loading({ content: 'Action in progress...', key: loadingMessageKey, duration: 0 });
      },
      onSuccess: (data) => {
        message.destroy(loadingMessageKey);
        message.success(data.message, 2);

        queryClient.invalidateQueries(oktaQueryKey);
      },
      onError: (error: AxiosError) => {
        message.destroy(loadingMessageKey);
        const errorMessage =
          error.response?.data?.message || error.message || 'Something went wrong';
        message.error(errorMessage, messageTime);
      },
      onSettled: () => {
        setTimeout(() => {
          message.destroy(); // Hide the loading message
        }, messageSettleTime);
      },
    },
  );
};
